import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getMediaKey, isMediaKeyError } from 'api/media';
import { isTouchDevice } from 'utils/screen';
import { READER_THEMES } from "utils/themes";
import { CONTENT_WIDTH } from 'utils/themes/sizes';
import { ebookDataPropType, queuePropType } from 'utils/prop-types';
import useMediaQuery from 'hook/use-media-query';
import useWindowSize from 'hook/use-window-size';
import Box from '@material-ui/core/Box';
import Fade from '@material-ui/core/Fade';
import IconButton from '@material-ui/core/IconButton';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import FullScreenHeader from 'components/full-screen-components/header';
import Loader from 'components/loader';
import MediaErrorDialog from 'components/common/media-error-dialog';
import clsx from 'clsx';
import { useSwipeable } from 'react-swipeable';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { TransformWrapper, TransformComponent } from 'components/zoom-pan-pinch';
import useStyles from './pdf-reader.styles';
import EbookTimeline from '../components/ebook-timeline';
import ControlPanel from './components/control-panel';
import { extractTransformersUidFromMediaKey } from "api/media/media.utils";
import combineURLs from 'axios/lib/helpers/combineURLs';
import firebase from "utils/auth/firebase";
import { processData } from "utils/content";

const removeTextLayerOffset = () => {
  const textLayers = document.querySelectorAll('.react-pdf__Page__textContent');
  textLayers.forEach((layer) => {
    const { style } = layer;
    style.top = '0';
    style.left = '0';
    style.transform = '';
  });
};

const PDFReader = memo((props) => {
  const isTablet = useMediaQuery('tablet');

  const {
    queue,
    config: { readerAllowPrint, readerAllowCopy },
    ebookData: { currentPage, searchHighlight },
    activeIndex,
    resetPlayerAction,
    changeEbookReaderOpenAction,
    updateEBookDataAction,
  } = props;

  const { width, height } = useWindowSize();
  const isTouchableDevice = isTouchDevice();

  const activeQueueItem = queue[activeIndex];

  const readerViewRef = useRef();
  const pageRef = useRef();
  const documentRef = useRef();
  const transformRef = useRef();
  const headerRef = useRef(null);
  const shouldFocusHeaderRef = useRef(false);
  const [pdf, setPdf] = useState();
  const [file, setFile] = useState();
  const [scale, setScale] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [isEBookLoaded, setIsEBookLoaded] = useState(false);
  const [initialCoordinates, setInitialCoordinates] = useState([0, 0]);
  const [isDistractionFreeMode, setIsDistractionFreeMode] = useState(false);
  const [mediaError, setMediaError] = useState('');

  const dismissMediaError = useCallback(() => {
    setMediaError('');
    resetPlayerAction();
  }, [resetPlayerAction]);

  const pageMaxHeight = useMemo(() => height - 210, [height]);
  const pageMaxWidth = useMemo(() => Math.min(CONTENT_WIDTH, width) - (isTablet ? 45 : 125), [width, isTablet]);

  const classes = useStyles({ activeTheme: READER_THEMES.light });

  const pageSizes = useMemo(() => {
    const sizes = { height: pageMaxHeight };

    if (pageMaxHeight > pageMaxWidth * 1.3) {
      sizes.width = pageMaxWidth;
    }

    return sizes;
  }, [isPageLoaded, pageMaxWidth, pageMaxHeight]);

  const goToPrevPage = useCallback(() => {
    const currentPageNumber = +pageRef?.current?.dataset?.pageNumber;
    const newPageNumber = currentPageNumber > 1 ? currentPageNumber - 1 : currentPageNumber;

    updateEBookDataAction({ currentPage: newPageNumber });
  }, []);

  const goToNextPage = useCallback(() => {
    const currentPageNumber = +pageRef?.current?.dataset?.pageNumber;
    const totalPagesNumber = +pageRef?.current?.dataset?.totalPages;
    const newPageNumber = currentPageNumber < totalPagesNumber ? currentPageNumber + 1 : currentPageNumber;

    updateEBookDataAction({ currentPage: newPageNumber });
  }, []);

  const closeEbookReader = useCallback(() => {
    changeEbookReaderOpenAction(false);
  }, []);

  const handleSwipeLeft = useCallback(
    (event) => {
      if (scale === 1 && event?.absX >= 100) {
        goToNextPage();
      }
    },
    [scale, goToNextPage],
  );

  const handleSwipeRight = useCallback(
    (event) => {
      if (scale === 1 && event?.absX >= 100) {
        goToPrevPage();
      }
    },
    [scale, goToPrevPage],
  );

  // Handlers for touchable devices
  const handlers = useSwipeable({
    onSwipedLeft: handleSwipeLeft,
    onSwipedRight: handleSwipeRight,
  });

  const handleHeaderKeyDownCapture = useCallback((event) => {
    if (event.key === 'Escape') {
      setIsDistractionFreeMode(true);
      shouldFocusHeaderRef.current = false;
    }
  }, []);

  // Keyboard listener for previousPage/nextPage navigation
  const handleKeydown = useCallback((event) => {
    switch (event.code) {
      case 'ArrowLeft': {
        goToPrevPage();
        break;
      }
      case 'ArrowRight': {
        goToNextPage();
        break;
      }
      case 'Tab': {
        shouldFocusHeaderRef.current = true;
        setIsDistractionFreeMode(false);
        break;
      }
      case 'Escape': {
        closeEbookReader();
        break;
      }
      default:
        break;
    }
  }, []);

  const loadEbookAsset = useCallback(() => {
    const { downloadUrl } = activeQueueItem;

    if (downloadUrl) {
      getMediaKey(downloadUrl)
        .then(async ({ key }) => {
          const transformersUuid = extractTransformersUidFromMediaKey(key);
          const isTransformersPdf = !!transformersUuid;
          if (isTransformersPdf) {
            setIsLoading(true);
            const token = await firebase?.auth()?.currentUser?.getIdToken?.();
            const url = combineURLs(key, `${transformersUuid}.pdf`);
            const requestConfig = {
              headers: { authorization: `Firebase ${token}` },
            }
            await fetch(url, requestConfig)
              .then(response => response.arrayBuffer())
              .then(arrayBuffer => processData(arrayBuffer))
              .then(fileBytes => {
                  setFile(fileBytes);
                  setIsLoading(false);
              })
          } else if (key) {
            setFile(key);
            setIsLoading(false);
          }
        })
        .catch((error) => {
          // the download endpoint can answer with a message meant for the user
          if (isMediaKeyError(error)) {
            setIsLoading(false);
            setMediaError(error.message);
          } else {
            resetPlayerAction();
          }
        });
    }
  }, [activeQueueItem]);

  const handleEBookItemClick = useCallback(({ pageNumber }) => {
    updateEBookDataAction({ currentPage: pageNumber });
  }, []);

  const handleDocumentLoadSuccess = useCallback(
    (pdfInstance) => {
      setPdf(pdfInstance);
      setIsEBookLoaded(true);
      pageRef.current.dataset.totalPages = pdfInstance.numPages;
      const initialPage = activeQueueItem?.progress || 1;
      pageRef.current.dataset.currentPage = initialPage;
      updateEBookDataAction({
        totalPages: pdfInstance.numPages,
        currentPage: initialPage,
      });
    },
    [activeQueueItem],
  );

  const handlePageLoadSuccess = useCallback(() => {
    setIsPageLoaded(true);
    removeTextLayerOffset();
  }, []);

  // init reader
  useEffect(() => {
    loadEbookAsset();

    document.addEventListener('keydown', handleKeydown);
    // Clean up
    return () => {
      updateEBookDataAction({ totalPages: 0, currentPage: 1, skipHistoryUpdate: true });
      document.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  useEffect(() => {
    if (readerAllowCopy) {
      return undefined;
    }

    const preventCopy = (event) => {
      if (readerViewRef.current && window.getSelection()?.containsNode(readerViewRef.current, true)) {
        event.preventDefault();
      }
    };

    document.addEventListener('copy', preventCopy);
    document.addEventListener('cut', preventCopy);
    return () => {
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('cut', preventCopy);
    };
  }, [readerAllowCopy]);

  /** highlight search results or just render the text **/
  const customTextRenderer = useCallback(
    ({ str }) => {
        if (searchHighlight) {
            const searchHighlightRegex = new RegExp(`\\b${searchHighlight}\\b`, 'ig');
            const matches = str.match(searchHighlightRegex);
            if (matches) {
                return (
                    <span
                        className={clsx(!!str.trim() && classes.emptySelection)}
                        dangerouslySetInnerHTML={{
                            __html: str.replace(
                                searchHighlightRegex,
                                `<mark class="${classes.highlight}">${searchHighlight}</mark>`
                            )
                        }}
                    />
                )
            }
        }
      return (
          <span className={clsx(!!str.trim() && classes.emptySelection)}>
            {str}
          </span>
      )
    },
    [searchHighlight, classes],
  );

  useEffect(() => {
    if (isDistractionFreeMode || !shouldFocusHeaderRef.current) {
      return;
    }
    shouldFocusHeaderRef.current = false;
    requestAnimationFrame(() => {
      headerRef.current?.querySelector('button:not([disabled])')?.focus();
    });
  }, [isDistractionFreeMode, shouldFocusHeaderRef.current]);

  const renderControlPanel = useCallback(() => (
      isEBookLoaded ? <ControlPanel pdf={pdf} /> : null
  ), [pdf, isEBookLoaded]);

  const handleTimelineChange = useCallback((newPageNumber) => {
    updateEBookDataAction({ currentPage: newPageNumber });
  }, []);

  const handlePanningStart = useCallback((_, event) => {
    if (isTablet) {
      setInitialCoordinates([event?.touches?.[0]?.clientX, event?.touches?.[0]?.clientY]);
    } else {
      setInitialCoordinates([event.x, event.y]);
    }
  }, []);

  const handlePageClick = useCallback(
    (_, event) => {
      const tagName = event?.target?.tagName ?? '';
      const isLink = tagName.toLowerCase() === 'a';

      if (!isLink) {
        if (isTablet) {
          setIsDistractionFreeMode((current) => !current);
        } else if (scale === 1) {
          transformRef.current?.zoomIn();
        } else {
          transformRef.current?.zoomOut();
        }
      }
    },
    [scale, isTablet],
  );

  const handlePanningStop = useCallback(
    (ref, event) => {
      const currentX = isTablet ? event?.touches?.[0]?.clientX : event?.x;
      const currentY = isTablet ? event?.touches?.[0]?.clientY : event?.y;
      const [initialX, initialY] = initialCoordinates;
      const isClick = currentY === initialY && currentX === initialX;

      if (isClick) {
        handlePageClick(null, event);
      }
    },
    [scale, isTablet, handlePageClick, initialCoordinates],
  );

  const handlePageDoubleClick = useCallback(() => {
    if (isTablet) {
      transformRef.current?.zoomIn();
    }
  }, [isTablet]);

  const transformHandlers = useMemo(
    () =>
      isTablet
        ? {
            onClick: handlePageClick,
            onDoubleClick: handlePageDoubleClick,
          }
        : {
            onPanningStop: handlePanningStop,
          },
    [isTablet, handlePageClick, handlePanningStop, handlePageDoubleClick],
  );

  return (
    <>
      <Fade in={!isDistractionFreeMode}>
        <Box
          ref={headerRef}
          className={classes.readerHeaderContainer}
          onKeyDownCapture={handleHeaderKeyDownCapture}
        >
          <FullScreenHeader
            height={99}
            item={activeQueueItem}
            onClose={closeEbookReader}
            nameClassName={classes.textThemeColor}
            creatorClassName={classes.textThemeColor}
            renderControlPanel={renderControlPanel}
          />
        </Box>
      </Fade>
      <Box
        ref={readerViewRef}
        className={clsx(
          classes.readerViewContainer,
          !readerAllowPrint && classes.printHidden,
          !readerAllowCopy && classes.copyDisabled,
        )}
      >
        {!isTouchableDevice && (
          <Box className={clsx(classes.arrowBg, classes.leftArrow)}>
            <IconButton onClick={goToPrevPage} className={classes.arrowIconBtn}>
              <ArrowBackIosIcon className={classes.icon} />
            </IconButton>
          </Box>
        )}
        {!isTouchableDevice && (
          <Box className={clsx(classes.arrowBg, classes.rightArrow)}>
            <IconButton onClick={goToNextPage} className={classes.arrowIconBtn}>
              <ArrowForwardIosIcon className={classes.icon} />
            </IconButton>
          </Box>
        )}
        <Box {...handlers} className={classes.readerContainer}>
          {isLoading && <Loader />}
          {!isLoading &&
          <Document
            file={file}
            renderMode="svg"
            loading={<Loader />}
            inputRef={documentRef}
            externalLinkTarget="_blank"
            className={classes.readerView}
            onItemClick={handleEBookItemClick}
            onLoadSuccess={handleDocumentLoadSuccess}
          >
            <TransformWrapper
              ref={transformRef}
              {...transformHandlers}
              onPanningStart={handlePanningStart}
              panning={{ disabled: scale === 1 }}
              doubleClick={{ disabled: !isTablet }}
            >
              {({ state }) => {
                if (state?.scale >= 0) {
                  setScale(state.scale);
                }

                return (
                  <TransformComponent wrapperClass={classes.transformComponent}>
                    <Page
                      {...pageSizes}
                      inputRef={pageRef}
                      scale={state.scale}
                      loading={<Loader />}
                      pageNumber={currentPage}
                      onLoadSuccess={handlePageLoadSuccess}
                      customTextRenderer={customTextRenderer}
                      renderTextLayer={true}
                    />
                  </TransformComponent>
                );
              }}
            </TransformWrapper>
          </Document>}
        </Box>
      </Box>
      <EbookTimeline
        onChange={handleTimelineChange}
        textClassName={classes.textThemeColor}
        isDistractionFreeMode={isDistractionFreeMode}
        timelineContainerClassName={classes.timelineContainerPDF}
      />
      <MediaErrorDialog
        message={mediaError}
        titleKey="ebook_generic_error_dialog_title"
        onClose={dismissMediaError}
      />
    </>
  );
});

PDFReader.propTypes = {
  config: PropTypes.shape({
    readerAllowPrint: PropTypes.bool,
    readerAllowCopy: PropTypes.bool,
  }).isRequired,
  queue: queuePropType,
  ebookData: ebookDataPropType,
  activeIndex: PropTypes.number,
  resetPlayerAction: PropTypes.func.isRequired,
  updateEBookDataAction: PropTypes.func.isRequired,
  changeEbookReaderOpenAction: PropTypes.func.isRequired,
};

export default PDFReader;
